import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const HERO_PRESETS = [
  {
    label: 'Taller Moderno',
    url: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1920&q=80',
    thumb: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=200&q=60',
  },
  {
    label: 'Mecanico Pro',
    url: 'https://images.unsplash.com/photo-1625047509168-a7026f36de04?w=1920&q=80',
    thumb: 'https://images.unsplash.com/photo-1625047509168-a7026f36de04?w=200&q=60',
  },
  {
    label: 'Garaje Premium',
    url: 'https://images.unsplash.com/photo-1553440569-bcc63803a83d?w=1920&q=80',
    thumb: 'https://images.unsplash.com/photo-1553440569-bcc63803a83d?w=200&q=60',
  },
  {
    label: 'Auto Sport',
    url: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1920&q=80',
    thumb: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=200&q=60',
  },
];

export const SERVICES_CATALOG = [
  { id: 'mantenimiento', icon: '🔧', title: 'Mantenimiento Preventivo', desc: 'Cambio de aceite, filtros y revision general del vehiculo.' },
  { id: 'mecanica',      icon: '⚙️', title: 'Mecanica General',         desc: 'Motor, transmision, frenos y sistema de suspension.' },
  { id: 'electrico',     icon: '⚡', title: 'Sistema Electrico',         desc: 'Diagnostico computarizado y reparacion electrica.' },
  { id: 'aireacond',     icon: '❄️', title: 'Aire Acondicionado',        desc: 'Carga de gas, limpieza y reparacion del sistema.' },
  { id: 'diagnostico',   icon: '🔍', title: 'Diagnostico Profesional',   desc: 'Scanner de ultima generacion para detectar fallas.' },
  { id: 'lavado',        icon: '✨', title: 'Lavado y Detailing',         desc: 'Limpieza interior y exterior con productos premium.' },
  { id: 'alineacion',    icon: '🎯', title: 'Alineacion y Balanceo',      desc: 'Correccion de angulos y equilibrio de ruedas.' },
  { id: 'llantas',       icon: '🔵', title: 'Llantas y Rines',            desc: 'Venta, montaje y reparacion de llantas.' },
  { id: 'latoneria',     icon: '🎨', title: 'Latoneria y Pintura',        desc: 'Reparacion de golpes y pintura profesional.' },
  { id: 'frenos',        icon: '🛑', title: 'Sistema de Frenos',          desc: 'Pastillas, discos, liquido y calibradores.' },
  { id: 'transmision',   icon: '🔩', title: 'Transmision',                desc: 'Caja automatica, mecanica y diferencial.' },
  { id: 'suspension',    icon: '🚗', title: 'Suspension y Direccion',     desc: 'Amortiguadores, rotulas y direccion hidraulica.' },
  { id: 'vidrios',       icon: '🪟', title: 'Vidrios y Polarizado',       desc: 'Instalacion y reparacion de vidrios.' },
  { id: 'escape',        icon: '💨', title: 'Escape y Turbo',             desc: 'Sistema de escape, catalizadores y turbo.' },
  { id: 'bateria',       icon: '🔋', title: 'Baterias',                   desc: 'Venta, carga y mantenimiento de baterias.' },
];

const SectionTitle = ({ number, title, subtitle }) => (
  <div className="flex items-start gap-4 mb-6">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-600 text-white text-sm font-black flex items-center justify-center">
      {number}
    </div>
    <div>
      <h3 className="font-bold text-gray-900 text-base leading-tight">{title}</h3>
      <p className="text-gray-400 text-xs mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const WorkshopSetup = () => {
  const { workshopId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [workshop, setWorkshop] = useState(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [successData, setSuccessData] = useState(null); // { url, slug }
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    hero_title: '',
    hero_description: '',
    brand_color_primary: '#1e293b',
    brand_color_secondary: '#3b82f6',
    hero_image_url: HERO_PRESETS[0].url,
    footer_tagline: '',
    whatsapp: '',
    services: [],
  });

  useEffect(() => {
    const fetchWorkshop = async () => {
      try {
        const { data, error } = await supabase
          .from('workshops')
          .select('id, name, slug, city, address, phone, opening_time, closing_time')
          .eq('id', workshopId)
          .single();
        if (error) throw error;
        setWorkshop(data);
      } catch (err) {
        console.error('Error cargando taller:', err.message);
      } finally {
        setFetching(false);
      }
    };
    fetchWorkshop();
  }, [workshopId]);

  const set = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const toggleService = (id) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(id)
        ? prev.services.filter(s => s !== id)
        : [...prev.services, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.services.length === 0) {
      alert('Selecciona al menos un servicio que ofrece tu taller.');
      setActiveTab('servicios');
      return;
    }
    setLoading(true);
    try {
      const { data: updated, error } = await supabase
        .from('workshops')
        .update({
          hero_title: formData.hero_title,
          hero_description: formData.hero_description,
          brand_color_primary: formData.brand_color_primary,
          brand_color_secondary: formData.brand_color_secondary,
          hero_image_url: formData.hero_image_url,
          footer_tagline: formData.footer_tagline,
          whatsapp: formData.whatsapp,
          services: formData.services,
        })
        .eq('id', workshopId)
        .select();

      if (error) throw error;
      if (!updated || updated.length === 0) {
        throw new Error('La base de datos no actualizo el taller. Verifica los permisos RLS en Supabase (UPDATE policy para la tabla workshops).');
      }
      const landingUrl = `${window.location.origin}/taller/${workshop.slug}`;
      setSuccessData({ url: landingUrl, slug: workshop.slug, name: workshop.name });
    } catch (err) {
      console.error('Error guardando configuracion:', err.message);
      alert('Error al guardar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500">Cargando tu taller...</p>
        </div>
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500 text-lg">Taller no encontrado.</p>
      </div>
    );
  }

  const copyUrl = () => {
    navigator.clipboard.writeText(successData.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  // ── Pantalla de éxito ──
  if (successData) {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&color=000000&bgcolor=ffffff&data=${encodeURIComponent(successData.url)}`;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl shadow-gray-200/80 border border-gray-100 w-full max-w-md p-8 text-center">

          {/* Icono éxito */}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>

          <h1 className="text-2xl font-black text-gray-900 mb-1">Pagina publicada</h1>
          <p className="text-gray-400 text-sm mb-6">
            <span className="font-semibold text-gray-600">{successData.name}</span> ya esta en linea
          </p>

          {/* QR Code */}
          <div className="bg-gray-50 rounded-2xl p-5 mb-5 border border-gray-100">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Codigo QR de tu pagina</p>
            <img
              src={qrUrl}
              alt="QR Code"
              className="w-44 h-44 mx-auto rounded-xl"
            />
            <p className="text-xs text-gray-400 mt-3">Comparte este QR con tus clientes para que agenden directamente</p>
          </div>

          {/* URL */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 mb-5">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Link de tu pagina</p>
            <p className="text-orange-600 font-bold text-sm break-all">{successData.url}</p>
          </div>

          {/* Botones */}
          <div className="flex flex-col gap-3">
            <button
              onClick={copyUrl}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Link copiado
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  Copiar link
                </>
              )}
            </button>

            <button
              onClick={() => navigate(`/taller/${successData.slug}`)}
              className="w-full py-3 rounded-xl font-bold text-sm bg-orange-600 hover:bg-orange-500 text-white transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-600/20"
            >
              Ver mi pagina web
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-5">
            Guarda el QR o el link — es el acceso directo de tus clientes para agendar
          </p>
        </div>
      </div>
    );
  }

  const primary = formData.brand_color_primary;
  const secondary = formData.brand_color_secondary;
  const heroImg = formData.hero_image_url;

  const TABS = [
    { id: 'hero',      label: 'Texto',     icon: '✏️' },
    { id: 'imagen',    label: 'Imagen',    icon: '🖼️' },
    { id: 'servicios', label: 'Servicios', icon: '🛠️' },
    { id: 'colores',   label: 'Colores',   icon: '🎨' },
    { id: 'footer',    label: 'Footer',    icon: '📋' },
  ];

  const selectedServices = SERVICES_CATALOG.filter(s => formData.services.includes(s.id));

  return (
    <div className="min-h-screen bg-gray-50 font-sans">

      {/* TOP BAR */}
      <div className="bg-white border-b border-gray-100 px-6 py-3.5 flex items-center justify-between shadow-sm sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm leading-tight">Garaje Pro 360</p>
            <p className="text-gray-400 text-xs">Personaliza tu pagina web</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs bg-orange-100 text-orange-600 font-semibold px-3 py-1 rounded-full hidden sm:block">Paso 2 de 2</span>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white text-sm transition-all ${
              loading ? 'bg-gray-300 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-500 shadow-md shadow-orange-600/20'
            }`}
          >
            {loading ? 'Publicando...' : (
              <>
                Publicar pagina
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* WELCOME */}
        <div className="mb-8">
          <h1 className="text-2xl font-black text-gray-900">
            Hola, <span className="text-orange-600">{workshop.name}</span> 👋
          </h1>
          <p className="text-gray-500 mt-1">Tu taller esta registrado. Ahora crea tu pagina web en 5 pasos.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">

          {/* FORM */}
          <div className="lg:col-span-2 space-y-4">

            {/* TABS */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="grid grid-cols-5 border-b border-gray-100">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 text-xs font-bold flex flex-col items-center gap-1 transition-colors relative ${
                      activeTab === tab.id
                        ? 'text-orange-600 bg-orange-50 border-b-2 border-orange-600'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <span className="text-base">{tab.icon}</span>
                    {tab.label}
                    {tab.id === 'servicios' && formData.services.length > 0 && (
                      <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-orange-500 rounded-full text-white text-xs flex items-center justify-center font-black leading-none">
                        {formData.services.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="p-6">

                {/* TAB: TEXTO */}
                {activeTab === 'hero' && (
                  <div className="space-y-5">
                    <SectionTitle number="1" title="Texto principal" subtitle="Lo primero que vera el cliente al entrar" />
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Titulo de tu pagina</label>
                      <input
                        type="text"
                        placeholder="Ej: Expertos en mecanica automotriz en Chia"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                        value={formData.hero_title}
                        onChange={(e) => set('hero_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Descripcion / Slogan</label>
                      <textarea
                        rows={3}
                        placeholder="Ej: Cuidamos tu vehiculo con tecnologia de punta y garantia en cada trabajo."
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm resize-none"
                        value={formData.hero_description}
                        onChange={(e) => set('hero_description', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* TAB: IMAGEN */}
                {activeTab === 'imagen' && (
                  <div className="space-y-5">
                    <SectionTitle number="2" title="Imagen de fondo" subtitle="Elige la foto que mas represente tu taller" />
                    <div className="grid grid-cols-2 gap-3">
                      {HERO_PRESETS.map((preset) => (
                        <button
                          key={preset.url}
                          type="button"
                          onClick={() => set('hero_image_url', preset.url)}
                          className={`relative rounded-xl overflow-hidden border-2 transition-all group ${
                            formData.hero_image_url === preset.url
                              ? 'border-orange-500 shadow-lg shadow-orange-500/20'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <img src={preset.thumb} alt={preset.label} className="w-full h-20 object-cover" />
                          <div className={`absolute inset-0 flex items-end p-2 ${
                            formData.hero_image_url === preset.url ? 'bg-orange-600/40' : 'bg-black/20 group-hover:bg-black/30'
                          }`}>
                            <span className="text-white text-xs font-bold">{preset.label}</span>
                          </div>
                          {formData.hero_image_url === preset.url && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">O pega el URL de tu propia foto</label>
                      <input
                        type="url"
                        placeholder="https://tu-imagen.com/foto.jpg"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                        value={HERO_PRESETS.some(p => p.url === formData.hero_image_url) ? '' : formData.hero_image_url}
                        onChange={(e) => e.target.value && set('hero_image_url', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* TAB: SERVICIOS */}
                {activeTab === 'servicios' && (
                  <div className="space-y-4">
                    <SectionTitle number="3" title="Servicios que ofreces" subtitle="Solo se mostraran los que selecciones" />

                    {/* Counter */}
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-400">
                        {formData.services.length === 0
                          ? 'Selecciona al menos 1 servicio'
                          : `${formData.services.length} servicio${formData.services.length > 1 ? 's' : ''} seleccionado${formData.services.length > 1 ? 's' : ''}`}
                      </p>
                      {formData.services.length > 0 && (
                        <button
                          type="button"
                          onClick={() => set('services', [])}
                          className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>

                    {/* Grid de servicios */}
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {SERVICES_CATALOG.map((service) => {
                        const selected = formData.services.includes(service.id);
                        return (
                          <button
                            key={service.id}
                            type="button"
                            onClick={() => toggleService(service.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${
                              selected
                                ? 'border-orange-400 bg-orange-50'
                                : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                            }`}
                          >
                            <span className="text-xl flex-shrink-0">{service.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-bold leading-tight ${selected ? 'text-orange-700' : 'text-gray-700'}`}>
                                {service.title}
                              </p>
                              <p className="text-xs text-gray-400 truncate mt-0.5">{service.desc}</p>
                            </div>
                            <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                              selected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                            }`}>
                              {selected && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* TAB: COLORES */}
                {activeTab === 'colores' && (
                  <div className="space-y-5">
                    <SectionTitle number="4" title="Identidad visual" subtitle="Los colores de tu marca" />
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Color del Header y Overlay</label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                        <input
                          type="color"
                          className="w-10 h-10 rounded-lg cursor-pointer border-none bg-transparent"
                          value={formData.brand_color_primary}
                          onChange={(e) => set('brand_color_primary', e.target.value)}
                        />
                        <div>
                          <p className="text-sm font-bold text-gray-800 font-mono">{formData.brand_color_primary}</p>
                          <p className="text-xs text-gray-400">Fondo del nav y degradado del hero</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Color de Botones y Accents</label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                        <input
                          type="color"
                          className="w-10 h-10 rounded-lg cursor-pointer border-none bg-transparent"
                          value={formData.brand_color_secondary}
                          onChange={(e) => set('brand_color_secondary', e.target.value)}
                        />
                        <div>
                          <p className="text-sm font-bold text-gray-800 font-mono">{formData.brand_color_secondary}</p>
                          <p className="text-xs text-gray-400">Botones de accion y destacados</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Combinaciones rapidas</p>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { p: '#1e293b', s: '#3b82f6', name: 'Azul Pro' },
                          { p: '#7c3aed', s: '#a855f7', name: 'Morado' },
                          { p: '#b91c1c', s: '#ef4444', name: 'Rojo' },
                          { p: '#065f46', s: '#10b981', name: 'Verde' },
                          { p: '#1e3a5f', s: '#f59e0b', name: 'Dorado' },
                          { p: '#111827', s: '#6366f1', name: 'Dark' },
                        ].map(c => (
                          <button
                            key={c.name}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, brand_color_primary: c.p, brand_color_secondary: c.s }))}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                          >
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: c.p }}></span>
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: c.s }}></span>
                            <span className="text-xs text-gray-500">{c.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB: FOOTER */}
                {activeTab === 'footer' && (
                  <div className="space-y-5">
                    <SectionTitle number="5" title="Pie de pagina" subtitle="Informacion al final de tu landing" />
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Slogan del footer</label>
                      <textarea
                        rows={2}
                        placeholder="Ej: Tu confianza es nuestro motor. Calidad garantizada desde 2010."
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm resize-none"
                        value={formData.footer_tagline}
                        onChange={(e) => set('footer_tagline', e.target.value)}
                      />
                      <p className="text-xs text-gray-400 mt-1">Aparece debajo del nombre en el footer</p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Numero de WhatsApp</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">+57</span>
                        <input
                          type="tel"
                          placeholder="300 000 0000"
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                          value={formData.whatsapp}
                          onChange={(e) => set('whatsapp', e.target.value)}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Se mostrara un boton verde flotante y en el footer</p>
                    </div>
                    {(formData.footer_tagline || formData.whatsapp) && (
                      <div className="bg-gray-900 rounded-xl p-4">
                        <p className="text-white font-black italic text-sm mb-1">{workshop.name}</p>
                        {formData.footer_tagline && <p className="text-gray-400 text-xs leading-relaxed">{formData.footer_tagline}</p>}
                        {formData.whatsapp && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 rounded-full text-white text-xs font-bold">
                            WhatsApp: {formData.whatsapp}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* URL info */}
            <div className="flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-orange-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              <div>
                <p className="text-xs text-gray-500">Tu pagina quedara disponible en:</p>
                <p className="text-xs font-black text-orange-600">/taller/{workshop.slug}</p>
              </div>
            </div>
          </div>

          {/* LIVE PREVIEW */}
          <div className="lg:col-span-3 sticky top-24">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Vista Previa en Tiempo Real</p>
              <span className="text-xs bg-green-100 text-green-600 font-semibold px-2.5 py-1 rounded-full">Live</span>
            </div>

            <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-xl bg-white">

              {/* Nav preview */}
              <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: primary }}>
                <span className="text-white font-black italic uppercase tracking-tight text-sm">{workshop.name}</span>
                <span className="text-white text-xs font-bold px-3 py-1.5 rounded-full" style={{ backgroundColor: secondary }}>
                  Agendar →
                </span>
              </div>

              {/* Hero preview */}
              <div
                className="relative h-44 flex items-center justify-center text-center overflow-hidden"
                style={{ backgroundImage: `url(${heroImg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
              >
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primary}ee, ${primary}99, rgba(0,0,0,0.6))` }} />
                <div className="relative z-10 px-4">
                  <h2 className="text-white font-black text-sm leading-tight mb-2">
                    {formData.hero_title || 'Tu titulo principal aqui'}
                  </h2>
                  <p className="text-white/70 text-xs mb-3 leading-relaxed line-clamp-2">
                    {formData.hero_description || 'Tu slogan aparecera aqui.'}
                  </p>
                  <span className="inline-block px-4 py-1.5 rounded-full text-white text-xs font-black" style={{ backgroundColor: secondary }}>
                    Agendar Cita →
                  </span>
                </div>
              </div>

              {/* Stats strip */}
              <div className="grid grid-cols-4 py-2.5" style={{ backgroundColor: primary }}>
                {['Garantia', 'Mismo dia', 'Certificados', 'Diagnostico'].map((s, i) => (
                  <div key={i} className="text-center text-white text-xs opacity-70 px-1">{s}</div>
                ))}
              </div>

              {/* Services preview */}
              <div className="bg-gray-50 px-4 py-5">
                <p className="font-black text-gray-900 text-xs text-center mb-3">Nuestros Servicios</p>
                {selectedServices.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {selectedServices.slice(0, 6).map((s) => (
                      <div key={s.id} className="bg-white rounded-lg p-2 text-center shadow-sm border border-gray-100">
                        <div className="text-base mb-0.5">{s.icon}</div>
                        <p className="text-xs text-gray-600 leading-tight">{s.title}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center">
                    <p className="text-gray-400 text-xs">Selecciona servicios en la pestana 🛠️</p>
                  </div>
                )}
                {selectedServices.length > 6 && (
                  <p className="text-center text-xs text-gray-400 mt-2">+{selectedServices.length - 6} servicios mas</p>
                )}
              </div>

              {/* Footer preview */}
              <div className="bg-gray-900 px-4 py-4">
                <p className="text-white font-black italic text-xs mb-1">{workshop.name}</p>
                <p className="text-gray-500 text-xs">
                  {formData.footer_tagline || `Tu taller de confianza en ${workshop.city}.`}
                </p>
                <p className="text-gray-700 text-xs mt-2">Potenciado por <span className="text-blue-400">Garaje Pro 360</span></p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default WorkshopSetup;
